import { Injectable } from '@angular/core';

import { UploadPurpose } from '@shared/models/Enums';
import { ApiUploadService, PresignedUploadResponse } from './api-upload.service';

/**
 * Resultado de una subida. Conserva la forma que devolvía el SDK de S3 (Location, Key)
 * para no tocar los componentes que ya leen result.Location.
 */
export interface UploadResult {
    Location: string;
    Key: string;
}

@Injectable({
    providedIn: 'root'
})
export class UploadService {

    constructor(private apiUploadService: ApiUploadService) {
    }

    /**
     * Sube la foto de perfil del cliente.
     * @param file Archivo elegido por el usuario.
     * @param awsKey Nombre con el que se guarda el archivo.
     */
    photoUpload(file: File, awsKey: string): Promise<UploadResult> {

        return this.upload(UploadPurpose.CustomerPhoto, file, awsKey);
    }

    /**
     * Sube la imagen de verificación de tarjeta.
     * @param file Archivo elegido por el usuario.
     * @param awsKey Nombre con el que se guarda el archivo.
     */
    customerCardUpload(file: File, awsKey: string): Promise<UploadResult> {

        return this.upload(UploadPurpose.CustomerCard, file, awsKey);
    }

    /**
     * Sube el adjunto de un bit.
     * @param file Archivo elegido por el usuario.
     * @param awsKey Nombre con el que se guarda el archivo.
     */
    bitAttachmentUpload(file: File, awsKey: string): Promise<UploadResult> {

        return this.upload(UploadPurpose.BitAttachment, file, awsKey);
    }

    /**
     * Pide el permiso de subida al API y sube el archivo directo a S3.
     *
     * El navegador ya no tiene credenciales de AWS: el API firma una URL de un solo uso,
     * acotada al propósito, al tipo y al tamaño exactos del archivo.
     *
     * @param purpose Destino lógico del archivo.
     * @param file Archivo a subir.
     * @param awsKey Nombre con el que se guarda el archivo.
     * @returns La URL pública y el key definitivo del objeto.
     */
    private async upload(purpose: UploadPurpose, file: File, awsKey: string): Promise<UploadResult> {

        const presigned = await this.apiUploadService.getPresignedUpload({
            purpose,
            fileName: awsKey,
            // Algunos navegadores reportan type vacío para archivos válidos; el API decide.
            contentType: file.type,
            sizeBytes: file.size
        });

        await this.putToStorage(presigned, file);

        return { Location: presigned.publicUrl, Key: presigned.objectKey };
    }

    /**
     * Sube el archivo a la URL prefirmada.
     *
     * Usa fetch y no HttpClient a propósito: los interceptores de la app agregan headers
     * de sesión que no tienen nada que hacer en un request a S3.
     *
     * @param presigned Permiso emitido por el API.
     * @param file Archivo a subir.
     */
    private async putToStorage(presigned: PresignedUploadResponse, file: File): Promise<void> {

        let response: Response;

        try {
            response = await fetch(presigned.uploadUrl, {
                method: 'PUT',
                // Debe coincidir exactamente con lo firmado, o S3 rechaza la subida.
                headers: { 'Content-Type': presigned.contentType },
                body: file
            });
        } catch {
            // fetch solo rechaza por fallas de red; el código lo esperan los componentes.
            const networkError: any = new Error('Error de red al subir el archivo');
            networkError.code = 'NetworkingError';
            throw networkError;
        }

        if (!response.ok) {
            throw new Error(`Error al subir el archivo (${response.status}).`);
        }
    }

    /**
     * Indica si el nombre corresponde a una imagen.
     * @param fileName Nombre del archivo.
     */
    public static isImage(fileName: string): boolean {

        const extensions = ['.gif', '.jpg', '.jpeg', '.png'] // you can add more extensions

        const found = extensions.some(ext => fileName.toLowerCase().includes(ext));

        return found;
    };

    /**
     * Indica si el nombre corresponde a un video.
     * @param fileName Nombre del archivo.
     */
    public static isVideo(fileName: string): boolean {

        const extensions = ['.mpg', '.mp2', '.mpeg', '.mpe', '.mpv', '.mp4'] //you can add more extensions

        const found = extensions.some(ext => fileName.toLowerCase().includes(ext));

        return found;
    };
}
